/**
 * Files a message carries, from the two places the gateway names them.
 *
 * A conversation can hold two very different kinds of file, and openclaw
 * reports them in two different shapes:
 *
 *  - what the USER attached, recorded on the transcript row as
 *    `media://inbound/<id>.<ext>` with a `contentType` beside it. Read by
 *    `projectGatewayHistory`.
 *  - what the AGENT produced — a chart it drew, a PNG it generated — which
 *    arrives on a live frame as absolute paths inside the pod:
 *
 *      ["/home/node/.openclaw/workspace/tmp/circle-100x100.png"]
 *
 *    with no type beside them, so the extension is all there is.
 *
 * This turns the second shape into the same `MessageAttachment` the first
 * produces, so a bubble renders both the same way and one fetch path can
 * serve them. Nothing read that field before, which is why an agent saying
 * "here it is" arrived as text with the file silently dropped.
 */
import type { MessageAttachment } from './types';

/** Extensions worth naming a type for; anything else is opaque bytes. */
const MEDIA_TYPES_BY_EXTENSION: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  pdf: 'application/pdf',
  csv: 'text/csv',
  json: 'application/json',
  txt: 'text/plain',
  md: 'text/markdown',
  // Deliberately NOT svg: it is a script host, and an attachment renders in
  // an `<img>` on the app's own origin. Served and labelled as bytes.
};

/**
 * Whether a media type is one the app will draw in an `<img>`.
 *
 * The one place that decides it. "Starts with `image/`" was written out
 * separately in three, and they disagreed: the allowlist above excludes SVG
 * deliberately — it is a script host, and an attachment renders on the app's
 * own origin — while a plain prefix test called `image/svg+xml` renderable
 * the moment a gateway named that type. So the exclusion held for a file
 * identified by its extension and not for one identified by the gateway.
 */
export function isRenderableImageType(mimeType: string): boolean {
  const base = (mimeType.split(';')[0] ?? '').trim().toLowerCase();
  if (!base.startsWith('image/')) return false;
  return base !== 'image/svg+xml';
}

/** The file name at the end of a path or URL, without its directories. */
function baseName(ref: string): string {
  const withoutQuery = ref.split(/[?#]/u)[0] ?? ref;
  const parts = withoutQuery.split(/[\\/]/u);
  return parts[parts.length - 1] ?? '';
}

/**
 * The type a reference's own extension implies, or null when it implies none.
 *
 * The last resort for deciding what a file is. openclaw names an attachment
 * `media://inbound/<uuid>.jpg` and USUALLY records a type beside it — but only
 * usually, and a turn that arrives without one used to render as an anonymous
 * "file" chip: no type meant not-an-image, not-an-image meant the picture was
 * never fetched, and the user saw a grey box next to their own photo. The
 * extension was sitting in the reference the whole time.
 */
export function mediaTypeFromRef(ref: string): string | null {
  if (typeof ref !== 'string') return null;
  const name = baseName(ref.trim());
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return null;
  return MEDIA_TYPES_BY_EXTENSION[name.slice(dot + 1).toLowerCase()] ?? null;
}

/**
 * One attachment for a file the agent produced, or null when the reference is
 * not usable.
 *
 * The type comes from the extension, since a frame carries none. An unknown
 * extension is still an attachment — the message did carry a file, and saying
 * so is better than dropping it — just not one claimed to be an image.
 */
export function attachmentFromMediaPath(ref: string): MessageAttachment | null {
  if (typeof ref !== 'string') return null;
  const trimmed = ref.trim();
  if (trimmed.length === 0) return null;
  const name = baseName(trimmed);
  if (name.length === 0) return null;
  const mimeType = mediaTypeFromRef(trimmed) ?? 'application/octet-stream';
  return {
    type: isRenderableImageType(mimeType) ? 'image' : 'file',
    mimeType,
    // The agent names its own files, so unlike an inbound attachment there IS
    // a name worth showing — "circle-100x100.png" says more than "image".
    fileName: name,
    // Kept verbatim: it is how the file is addressed when fetching it, and
    // the fetcher decides what it can do with a given shape.
    sourceUrl: trimmed,
  };
}

/**
 * Attachments for every file a frame named, in order, ignoring the ones it
 * named unusably. Empty when the frame carried none.
 */
export function attachmentsFromMediaPaths(
  refs: readonly string[] | undefined,
): readonly MessageAttachment[] {
  if (!Array.isArray(refs) || refs.length === 0) return [];
  const out: MessageAttachment[] = [];
  const seen = new Set<string>();
  for (const ref of refs) {
    const attachment = attachmentFromMediaPath(ref);
    if (attachment == null) continue;
    // A frame can repeat a path (a re-send of the same answer); the same file
    // twice in one bubble is a rendering bug, not two files.
    const key = attachment.sourceUrl ?? attachment.fileName ?? '';
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(attachment);
  }
  return out;
}
